import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment, { Moment as TMoment } from 'moment';
import posed from 'react-pose';
import { History } from 'history';
import Loader from 'react-loading';
import { themes, graphql as KBGQL, UserPeriodResponse, Vital } from 'kb-shared';
import { Calendar } from 'kb-web-shared';
import { graphql } from 'react-apollo';
import Honeybadger from '@honeybadger-io/js';

import { ReactComponent as PeriodTrackingIcon } from 'assets/icons/period-tracking.svg';
import { DateInput, CycleLengthInput, PredictedNextPeriod } from './InfoSectionComponents';
import { LastPeriod, CycleLength } from './components';
import FinishModal from './FinishModal';
import { YellowButton } from '../../Buttons';
import { PREGNANCY_CALCULATOR_QUERY } from '../PregnancyCalculatorWidget';

const client = KBGQL.client;

type Props = {
  history: History;
  isLoadingVital: boolean;
  userVital: Vital;
};

type Step = 'start' | 'cycle_length' | 'prediction' | 'actual_start_date';
type State = {
  step: Step;
  // Editable Info
  editedLastPeriodDate: string | null;
  editedDaysBetweenPeriod: number | null;
  logNewPeriodDate: string | null;
  // State for calendar
  currentMonth: TMoment;
  datePickerVisible: boolean;
  // Loading States
  updatingVitals: boolean;
  isInitialLoad: boolean;
  // After user logs a new period, show them a modal
  showCompleteModal: boolean;
};

const PeriodTrackerWidget = ({ isLoadingVital, userVital }: Props) => {
  const [state, setState] = useState<State>({
    step: 'start',
    editedLastPeriodDate: null,
    logNewPeriodDate: null,
    editedDaysBetweenPeriod: null,
    datePickerVisible: false,
    updatingVitals: false,
    showCompleteModal: false,
    isInitialLoad: true,
    currentMonth: moment()
  });
  useEffect(() => {
    if (!isLoadingVital && state.isInitialLoad) {
      // Initial load of data, setup state vars
      const vital: Vital = userVital;
      // If we have a last period date AND cycle length AND nextPeriodDate, start user at the 'prediction' step
      //TODO: Do we need to add date logic here to check if we are past the nextPeriodDate or not? Or will the server always keep that up to date?
      let step: Step = 'start';
      if (vital.periodDate) {
        step = 'cycle_length';
        // Check for both daysBetweenCycles& nextPeriodDate since daysBetweenCycle can exist without a nextPeriodDate
        // Unsure if that is a backend bug or old behavior ^
        // @ts-ignore
        const hasPrediction = vital.daysBetweenCycles > 0 && vital.nextPeriodDate;
        if (hasPrediction) {
          step = 'prediction';
        }
      }

      setState({
        ...state,
        editedDaysBetweenPeriod: vital.daysBetweenCycles,
        editedLastPeriodDate: vital.periodDate,
        step,
        isInitialLoad: false
      });
    }
  }, [isLoadingVital, state, userVital]);

  const createPeriod = (date: string): Promise<void> => {
    setState({
      ...state,
      updatingVitals: true
    });
    return client.mutate({
      mutation: KBGQL.mutate.CREATE_PERIOD,
      variables: {
        periodDate: date
      },
      refetchQueries: [{ query: KBGQL.query.USER_PERIOD }, { query: PREGNANCY_CALCULATOR_QUERY }]
    });
  };

  const startTracking = () => {
    if (state.editedLastPeriodDate) {
      setState({
        ...state,
        updatingVitals: true
      });
      // If we are updating/editing
      if (userVital.id) {
        client
          .mutate({
            mutation: KBGQL.mutate.UPDATE_PERIOD,
            variables: {
              id: userVital.id,
              periodDate: state.editedLastPeriodDate
            },
            refetchQueries: [
              { query: KBGQL.query.USER_PERIOD },
              { query: PREGNANCY_CALCULATOR_QUERY }
            ]
          })
          .then(() => {
            setState({
              ...state,
              updatingVitals: false,
              step: 'cycle_length'
            });
          })
          .catch(error => Honeybadger.notify(error));
      } else {
        // If we are creating a new period
        createPeriod(state.editedLastPeriodDate)
          .then(() => {
            setState({
              ...state,
              updatingVitals: false,
              step: 'cycle_length'
            });
          })
          .catch(error => Honeybadger.notify(error));
      }
    }
  };

  const updateCycleLength = () => {
    setState({
      ...state,
      updatingVitals: true
    });
    client
      .mutate({
        mutation: KBGQL.mutate.UPDATE_CYCLE_LENGTH,
        variables: {
          daysBetweenCycles: state.editedDaysBetweenPeriod
        },
        refetchQueries: [
          {
            query: KBGQL.query.USER_PERIOD
          }
        ]
      })
      .then(() => {
        setState({
          ...state,
          updatingVitals: false,
          step: 'prediction'
        });
      })
      .catch(error => Honeybadger.notify(error));
  };

  const goToLogPeriod = () => {
    setState({
      ...state,
      step: 'actual_start_date'
    });
  };

  const logNewPeriod = () => {
    if (state.logNewPeriodDate) {
      createPeriod(state.logNewPeriodDate)
        .then(() => {
          // After user logs a new period, show the prediction state
          setState({
            ...state,
            updatingVitals: false,
            editedLastPeriodDate: state.logNewPeriodDate,
            // Also render the modal that prompts them to book a new appointment
            showCompleteModal: true,
            step: 'prediction'
          });
        })
        .catch(error => Honeybadger.notify(error));
    }
  };

  const getButtonProps = (): { title: string; action: () => void } => {
    switch (state.step) {
      case 'start':
        return { title: 'Start tracking', action: startTracking };
      case 'cycle_length':
        return { title: 'Forecast next period', action: updateCycleLength };
      case 'prediction':
        return { title: 'Log your period', action: goToLogPeriod };
      case 'actual_start_date':
        return { title: 'Submit', action: logNewPeriod };
      default:
        return {
          title: '',
          action: () => {}
        };
    }
  };

  const showDatePicker = () => {
    setState({
      ...state,
      datePickerVisible: true
    });
  };

  const previousMonth = () => {
    setState({
      ...state,
      currentMonth: state.currentMonth.clone().add(-1, 'month')
    });
  };

  const nextMonth = () => {
    setState({
      ...state,
      currentMonth: state.currentMonth.clone().add(1, 'month')
    });
  };

  const renderCompleteModal = () => {
    return (
      <FinishModal
        visible={state.showCompleteModal}
        dismiss={() => {
          setState({
            ...state,
            showCompleteModal: false
          });
        }}
      />
    );
  };

  const renderDatePicker = () => {
    const pose = state.datePickerVisible ? 'show' : 'close';
    switch (state.step) {
      case 'start':
        return (
          <AnimatedCalContainer pose={pose}>
            <Calendar
              currentMonth={state.currentMonth}
              // @ts-ignore
              selectedDate={state.editedLastPeriodDate && moment(state.editedLastPeriodDate)}
              onPrevious={previousMonth}
              onNext={nextMonth}
              onSelectDate={(date: TMoment) => {
                setState({
                  ...state,
                  editedLastPeriodDate: date.toISOString(),
                  datePickerVisible: false
                });
              }}
              renderBorder={false}
            />
          </AnimatedCalContainer>
        );
      case 'actual_start_date':
        return (
          <AnimatedCalContainer pose={pose}>
            <Calendar
              currentMonth={state.currentMonth}
              // @ts-ignore
              selectedDate={state.logNewPeriodDate && moment(state.logNewPeriodDate)}
              onPrevious={previousMonth}
              onNext={nextMonth}
              onSelectDate={(date: TMoment) => {
                setState({
                  ...state,
                  logNewPeriodDate: date.toISOString(),
                  datePickerVisible: false
                });
              }}
              renderBorder={false}
            />
          </AnimatedCalContainer>
        );
      default:
        return null;
    }
  };

  const renderCurrentState = () => {
    // Check if loading first
    if (isLoadingVital || state.updatingVitals) {
      return (
        <LoadContainer>
          <Loader type="spin" color={themes.colors.KBBlush} height={50} width={50} />
        </LoadContainer>
      );
    }

    switch (state.step) {
      case 'start':
        return (
          <React.Fragment>
            <DateInput
              title={'Last period start date'}
              currentDate={state.editedLastPeriodDate}
              onClick={showDatePicker}
            />
          </React.Fragment>
        );
      case 'cycle_length':
        return (
          <CycleLengthInput
            value={state.editedDaysBetweenPeriod}
            onChangeDays={value => {
              setState({
                ...state,
                editedDaysBetweenPeriod: value
              });
            }}
          />
        );
      case 'prediction':
        // @ts-ignore
        return <PredictedNextPeriod date={userVital.nextPeriodDate} />;
      case 'actual_start_date':
        return (
          <React.Fragment>
            <DateInput
              title={'Actual start date'}
              currentDate={state.logNewPeriodDate}
              onClick={showDatePicker}
            />
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  const renderLastPeriod = () => {
    if (!state.editedLastPeriodDate) {
      return null;
    }

    let poseState = 'close';
    let onEditState = 'start';
    if (state.step === 'cycle_length') {
      poseState = 'show';
    } else if (state.step === 'prediction') {
      poseState = 'show';
      onEditState = 'start';
    }

    return (
      <LastPeriod
        date={state.editedLastPeriodDate}
        pose={poseState}
        editVisible={true}
        // @ts-ignore
        onEditPress={() => setState({ ...state, step: onEditState })}
      />
    );
  };

  const renderCycleLength = () => {
    if (!state.editedLastPeriodDate) {
      return null;
    }

    let poseState = 'close';
    let onEditState: Step = 'cycle_length';
    if (state.step === 'cycle_length') {
      poseState = 'close';
    } else if (state.step === 'prediction') {
      poseState = 'show';
      onEditState = 'cycle_length';
    }

    return (
      <CycleLength
        daysBetweenCycles={state.editedDaysBetweenPeriod}
        pose={poseState}
        editVisible={true}
        onEditPress={() => setState({ ...state, step: onEditState })}
      />
    );
  };

  const buttonProps = getButtonProps();

  return (
    <Container>
      {renderCompleteModal()}
      <IconContainer>
        <PeriodTrackingIcon />
      </IconContainer>
      <TitleText>Period Tracker</TitleText>
      {renderLastPeriod()}
      {renderCycleLength()}
      <Description>
        Enter the first date of your last period and the average number of days your period lasts.
        Your team will use this information to understand your menstrual cycles.
      </Description>
      <InfoArea>
        <InfoAreaContent>{renderCurrentState()}</InfoAreaContent>
        {renderDatePicker()}
      </InfoArea>
      <ButtonSection>
        <YellowButton onClick={buttonProps.action}>{buttonProps.title}</YellowButton>
      </ButtonSection>
    </Container>
  );
};

// @ts-ignore
const withVitalInfo = graphql(KBGQL.query.USER_PERIOD, {
  props: ({ data }: { data: UserPeriodResponse }) => {
    return {
      isLoadingVital: data.loading,
      // @ts-ignore
      userVital: (data && data.lastPeriod) || {
        daysBetweenCycles: null,
        id: null,
        periodDate: null,
        nextPeriodDate: null
      }
    };
  }
});

export default withVitalInfo(PeriodTrackerWidget);

const Container = styled.div`
  ${({ theme }) => theme.grid.container};
  padding-top: 20px;
  padding-bottom: 28px;
  display: flex;
  flex: 1;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: ${themes.colors.beige};
`;

const IconContainer = styled.div`
  padding-top: 20px;
`;

const TitleText = styled.div`
  ${themes.fonts.DomaineDisplay}
  font-size: 24px;
  color: ${themes.colors.night};
  padding: 12px 0px 8px 0px;
`;

const Description = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: 300;
  line-height: 1.43;
  text-align: center;
  color: ${themes.colors.night};
`;

const InfoArea = styled.div`
  width: 100%;
  min-height: 96px;
  margin-top: 28px;

  & > div + div {
    background-color: white;
    box-shadow: -4px 4px 0 0 #343754;
    border: solid 2px #343754;
    & > div {
      max-width: unset;

      & > div,
      & > div:nth-child(2) > div {
        width: 100%;
      }

      & > div:nth-child(1) {
        padding-top: 8px;
      }
      & > div:nth-child(2) {
        padding-left: 8px;
        padding-right: 8px;
        padding-bottom: 8px;
      }

      & > div:nth-child(2) > div {
        justify-content: center;
      }
    }
  }
`;

const InfoAreaContent = styled.div`
  min-height: 95px;
  padding: 12px 16px;
  background-color: white;
  box-shadow: -4px 4px 0 0 ${themes.colors.night};
  border: solid 2px ${themes.colors.night};
`;

const ButtonSection = styled.div`
  min-height: 100px;
  margin-top: 28px;
  margin-bottom: 28px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
`;

const ANIMATION_DURATION = 300;
const AnimatedCalContainer = posed.div({
  show: {
    height: 'auto',
    overflow: 'hidden',
    flip: true,
    transition: { duration: ANIMATION_DURATION }
  },
  close: {
    height: '0px',
    overflow: 'hidden',
    transition: { duration: ANIMATION_DURATION }
  }
});

const LoadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
